import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'

import { Operator, PageData, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'

import { AccountGroupType } from '@model/AccountGroupType'
import { TradingAccount } from '@model/TradingAccount'
import { TransferTypeEnum } from '@model/TransactionDto'
import { TransactionLimitDto } from '@model/TransactionLimitDto'
import { WalletDto, WalletTypeEnum } from '@model/WalletDto'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'

import { TransferSuccessModal } from '../../modals/TransferSuccess.modal'
import { TransferForm, TransferValues, isWallet } from './TransferForm'

interface MatchProps {
  walletId?: string
  tradingAccountId?: string
}

export interface TransferPageProps {
  title: string
  limits: TransactionLimitDto[]
  description?: React.ReactNode
  wallets: WalletDto[]
  tradingAccounts?: PageData<TradingAccount> | undefined
  walletTypeFrom: WalletTypeEnum
  walletTypeTo: WalletTypeEnum[]

  onSuccess(): void

  onCancel(): void

  isFromCampaign?: boolean
  transferFromLabel?: string
}

export const WalletTransferPage: React.FC<TransferPageProps> = (props) => {
  const {
    title,
    description,
    wallets = [],
    tradingAccounts = undefined,
    walletTypeFrom = WalletTypeEnum.CFD,
    walletTypeTo = [WalletTypeEnum.CFD],
    onSuccess,
    isFromCampaign,
    limits,
    transferFromLabel,
  } = props

  const { walletId, tradingAccountId }: MatchProps = useParams()
  const [searchParams] = useSearchParams()
  const toTradingAccountId = searchParams.get('toTradingAccountId') ?? undefined
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const [isSubmitting, setSubmitting] = useState(false)
  const entity = useSessionEntity()
  const transferSuccessModal = useModal(TransferSuccessModal, { onClose: onSuccess })

  const processTransfer = (values: TransferValues) => {
    const { transferFrom, transferTo } = values

    if (isWallet(transferFrom) && isWallet(transferTo)) {
      return walletToWalletTransfer(values)
    } else if (isWallet(transferFrom)) {
      return walletToTradingAccountTransfer(values)
    } else if (!isWallet(transferFrom)) {
      return tradingAccountToWalletTransfer(values)
    }
  }

  const walletToWalletTransfer = ({ transferFrom, transferTo, fromAmount }: TransferValues) => {
    return apiClient.transferWalletToWallet({
      caller: 'ca',
      FromWalletId: transferFrom.id,
      ToWalletId: transferTo.id,
      FromAmount: fromAmount,
      clientIpAddress: '',
      referrer_url: '',
    })
  }

  const walletToTradingAccountTransfer = ({
    transferFrom,
    transferTo,
    fromAmount,
  }: TransferValues) => {
    return apiClient.transferWalletToTradingAccount({
      FromWalletId: transferFrom.id,
      ToTaId: transferTo.id,
      Amount: fromAmount,
      clientIpAddress: '',
      referrer_url: '',
    })
  }

  const tradingAccountToWalletTransfer = ({
    transferFrom,
    transferTo,
    fromAmount,
  }: TransferValues) => {
    const campaignId = transferFrom.campaigns?.[0]?.id
    return apiClient.transferTradingAccountToWallet({
      ToWalletId: transferTo.id,
      FromTaId: transferFrom.id,
      Amount: fromAmount,
      clientIpAddress: '',
      referrer_url: '',
      campaignId: campaignId && campaignId !== '' ? campaignId : null,
    })
  }

  const handleSubmit = async (values: TransferValues) => {
    try {
      setSubmitting(true)

      const { transferFrom, transferTo } = values
      let currentTransferType: TransferTypeEnum

      if (isWallet(transferFrom) && isWallet(transferTo)) {
        currentTransferType = TransferTypeEnum.WALLET_TO_WALLET
      } else if (isWallet(transferFrom) && !isWallet(transferTo)) {
        currentTransferType = TransferTypeEnum.WALLET_TO_TA
      } else {
        currentTransferType = TransferTypeEnum.TA_TO_WALLET
      }

      const masterTransaction = await processTransfer(values)
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.tradingAccount.totalsAll() })

      transferSuccessModal.open({ masterTransaction, transferType: currentTransferType })
    } catch (e: unknown) {
      console.error(e)
    } finally {
      setSubmitting(false)
    }
  }

  const { t } = useTranslation()

  return (
    <Loading text={t('Wallet.Transferring...')} isLoading={isSubmitting} showLoadingIcon>
      <TransferForm
        title={title}
        entity={entity}
        description={description}
        limits={limits}
        wallets={wallets}
        tradingAccounts={tradingAccounts}
        walletId={walletId}
        walletTypeFrom={walletTypeFrom}
        walletTypeTo={walletTypeTo}
        tradingAccountId={tradingAccountId}
        toTradingAccountId={toTradingAccountId}
        handleFormSubmit={handleSubmit}
        isFromCampaign={isFromCampaign}
        transferFromLabel={transferFromLabel}
      />
    </Loading>
  )
}

interface WalletTransferFetchResult {
  wallets: WalletDto[]
  tradingAccounts: PageData<TradingAccount> | undefined
  limits: TransactionLimitDto[] | undefined
  isLoading: boolean
}
export const useWalletTransferFetch = (): WalletTransferFetchResult => {
  const apiClient = useApiClient(ClientApiClient)

  const { data = [], isLoading } = useQuery({
    queryKey: queryKeys.walletExtended.transferData({ type: 'cfd' }),
    queryFn: () =>
      Promise.all([
        apiClient.getWallets(),
        apiClient.getTransactionLimits(),
        apiClient.getTradingAccounts({
          pageSize: 150,
          search: {
            AccountGroupType: {
              value: AccountGroupType.Live,
              operator: Operator.EQUAL,
            },
            Platform: {
              value: 3,
              operator: Operator.NOT_EQUAL,
            },
            Status: {
              value: 1,
              operator: Operator.EQUAL,
            },
          },
        }),
      ]),
    refetchOnMount: 'always',
  })
  const [wallets = [], limits, tradingAccounts] = data

  return { wallets, tradingAccounts, limits, isLoading }
}

interface TradersRoomWalletTransferPageProps {
  walletType: WalletTypeEnum
}

export const TradersRoomWalletTransferPage: React.FC<TradersRoomWalletTransferPageProps> = ({
  walletType,
}) => {
  useScrollToTop()

  const navigate = useNavigate()
  const { t } = useTranslation()

  const { wallets, tradingAccounts, isLoading, limits = [] } = useWalletTransferFetch()

  const handleSuccess = () => navigate('/dashboard/traders-room/transaction-history')
  const handlePageExitConfirmation = () => navigate(CommonPaths.TradersRoomWallets)

  return (
    <Loading isLoading={isLoading}>
      <WalletTransferPage
        limits={limits}
        title={''}
        description={
          <>
            <span>{t('Wallet.Important!')}</span>{' '}
            {t(
              'Wallet.You can transfer funds only between wallets or wallet and trading accounts with same currency'
            )}
          </>
        }
        wallets={wallets}
        tradingAccounts={tradingAccounts}
        walletTypeFrom={walletType}
        walletTypeTo={[walletType]}
        onSuccess={handleSuccess}
        onCancel={handlePageExitConfirmation}
      />
    </Loading>
  )
}

interface TradersRoomWalletStartTransferPageProps {
  walletType: WalletTypeEnum
}

export const TradersRoomWalletStartTransferPage: React.FC<
  TradersRoomWalletStartTransferPageProps
> = (props) => {
  const { walletType } = props

  const navigate = useNavigate()

  const { wallets, tradingAccounts, isLoading, limits = [] } = useWalletTransferFetch()

  const handleSuccess = () => navigate('/dashboard/traders-room/transaction-history')

  const handleCancel = () => navigate(CommonPaths.TradersRoomWallets)

  return (
    <Loading isLoading={isLoading}>
      <WalletTransferPage
        title={''}
        limits={limits}
        wallets={wallets}
        tradingAccounts={tradingAccounts}
        walletTypeFrom={walletType}
        walletTypeTo={[walletType]}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </Loading>
  )
}

export const CampaignWalletTransferPage: React.FC = () => {
  const navigate = useNavigate()

  const { wallets, tradingAccounts, isLoading, limits = [] } = useWalletTransferFetch()

  const handleSuccess = () => navigate('/dashboard/traders-room/trading-accounts')

  const handleCancel = () => navigate(CommonPaths.TradersRoomWallets)

  const { t } = useTranslation()

  return (
    <Loading isLoading={isLoading}>
      <WalletTransferPage
        title={''}
        limits={limits}
        wallets={wallets}
        tradingAccounts={tradingAccounts}
        walletTypeFrom={WalletTypeEnum.CFD}
        walletTypeTo={[WalletTypeEnum.CFD]}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        isFromCampaign={true}
        transferFromLabel={t('Wallet.Transfer From Campaign Account')}
      />
    </Loading>
  )
}
